import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Table, Space, Popconfirm, message } from 'antd';
import { PlusOutlined, DeleteOutlined, FileTextOutlined } from '@ant-design/icons';
import { SectionHeader } from '../SectionHeader';
import { colors, spacing } from '../../design-system/tokens';
import { Typography } from '../../design-system/typography';
import { SmartSnippetBuilder, GeneratedClauses, ShipmentContext } from '../../shipment';

const { Option } = Select;

// No longer needed - dropdown options removed

export interface ShipmentGoodsRow {
  id: string;
  shipment: string;
  containers: string;
  units: string;
  description: string;
  price: string;
  buyerPrice: string;
  origin: string;
  // Shipping clause fields
  shipmentNoteOne?: string;
  shipmentNoteTwo?: string;
  packingNotes?: string;
  carrierId?: string;
  carrierName?: string;
}

interface ContractShipmentsGoodsFormProps {
  onDataChange?: (rows: ShipmentGoodsRow[]) => void;
  contractContext?: {
    buyer?: { id: string; name: string; code: string };
    supplier?: { id: string; name: string; code: string };
  };
}

// Validation functions
const validateContainers = (value: string): boolean => {
  const containerRegex = /^\d{1,2}x[A-Za-z0-9]{2,9}$/;
  return containerRegex.test(value);
};

const validateUnits = (value: string): boolean => {
  const unitsRegex = /^\d{1,4}x\d{1,3}# [A-Za-z\s]{3,10}$/;
  return unitsRegex.test(value);
};

const validatePrice = (value: string): boolean => {
  const priceRegex = /^\d+\.?\d*\/[A-Za-z]+$/;
  return priceRegex.test(value);
};

export const ContractShipmentsGoodsForm: React.FC<ContractShipmentsGoodsFormProps> = ({ 
  onDataChange, 
  contractContext 
}) => {
  const [rows, setRows] = useState<ShipmentGoodsRow[]>([
    {
      id: '1',
      shipment: '',
      containers: '',
      units: '',
      description: '',
      price: '',
      buyerPrice: '',
      origin: ''
    }
  ]);

  // Smart Snippet Builder state
  const [snippetBuilderVisible, setSnippetBuilderVisible] = useState(false);
  const [currentShipmentId, setCurrentShipmentId] = useState<string | null>(null);
  const [shipmentContext, setShipmentContext] = useState<ShipmentContext | null>(null);

  // Notify parent component when rows change
  useEffect(() => {
    if (onDataChange) {
      onDataChange(rows);
    }
  }, [rows, onDataChange]);

  // Debug: Log when component mounts
  useEffect(() => {
    console.log('ContractShipmentsGoodsForm mounted');
    return () => {
      console.log('ContractShipmentsGoodsForm unmounted');
    };
  }, []);

  const addRow = () => {
    const newRow: ShipmentGoodsRow = {
      id: Date.now().toString(),
      shipment: '',
      containers: '',
      units: '',
      description: '',
      price: '',
      buyerPrice: '',
      origin: ''
    };
    setRows([...rows, newRow]);
  };

  const removeRow = (id: string) => {
    if (rows.length > 1) {
      setRows(rows.filter(row => row.id !== id));
    }
  };

  const updateRow = (id: string, field: keyof ShipmentGoodsRow, value: any) => {
    setRows(rows.map(row => 
      row.id === id ? { ...row, [field]: value } : row
    ));
  };

  // Smart Snippet Builder handlers
  const handleGenerateClauses = (shipmentId: string) => {
    const shipmentRow = rows.find(row => row.id === shipmentId);
    if (!shipmentRow || !contractContext) {
      message.error('Cannot generate clauses: missing shipment or contract context');
      return;
    }

    // Extract commodities from the description field (simplified for demo)
    const commodities = shipmentRow.description ? 
      [{ id: '1', name: shipmentRow.description, code: shipmentRow.description }] :
      [{ id: '1', name: 'Mixed Goods', code: 'MIXED' }];

    // Build shipment context
    const context: ShipmentContext = {
      shipmentId: shipmentRow.id,
      buyer: contractContext.buyer,
      supplier: contractContext.supplier,
      carrier: shipmentRow.carrierId ? {
        id: shipmentRow.carrierId,
        name: shipmentRow.carrierName || 'Unknown Carrier',
        scac: shipmentRow.carrierId
      } : undefined,
      commodities,
      containerCount: parseInt(shipmentRow.containers?.split('x')[0] || '1')
    };

    setCurrentShipmentId(shipmentId);
    setShipmentContext(context);
    setSnippetBuilderVisible(true);
  };

  const handleSaveClauses = (clauses: GeneratedClauses) => {
    if (!currentShipmentId) return;

    updateRow(currentShipmentId, 'shipmentNoteOne', clauses.shipmentNoteOne || '');
    updateRow(currentShipmentId, 'shipmentNoteTwo', clauses.shipmentNoteTwo || '');
    updateRow(currentShipmentId, 'packingNotes', clauses.packingNotes || '');

    setSnippetBuilderVisible(false);
    setCurrentShipmentId(null);
    setShipmentContext(null);
    
    message.success('Contract clauses saved successfully');
  };


  const columns = [
    {
      title: 'Shipment',
      dataIndex: 'shipment',
      width: 150,
      render: (value: string, record: ShipmentGoodsRow) => (
        <Input
          value={value}
          onChange={(e) => updateRow(record.id, 'shipment', e.target.value)}
          placeholder="e.g., Sep '25/Oct '25"
          size="small"
        />
      )
    },
    {
      title: 'Containers',
      dataIndex: 'containers',
      width: 120,
      render: (value: string, record: ShipmentGoodsRow) => (
        <Input
          value={value}
          onChange={(e) => updateRow(record.id, 'containers', e.target.value)}
          placeholder="e.g., 2x20ft"
          size="small"
          status={value && !validateContainers(value) ? 'error' : undefined}
        />
      )
    },
    {
      title: 'Units',
      dataIndex: 'units',
      width: 140,
      render: (value: string, record: ShipmentGoodsRow) => (
        <Input
          value={value}
          onChange={(e) => updateRow(record.id, 'units', e.target.value)}
          placeholder="e.g., 720x50# cases"
          size="small"
          status={value && !validateUnits(value) ? 'error' : undefined}
        />
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: 200,
      render: (value: string, record: ShipmentGoodsRow) => (
        <Input
          value={value}
          onChange={(e) => updateRow(record.id, 'description', e.target.value)}
          placeholder="e.g., Nonpareil US Extra No. 1 (J-spec) 30/32"
          size="small"
        />
      )
    },
    {
      title: 'Price',
      dataIndex: 'price',
      width: 120,
      render: (value: string, record: ShipmentGoodsRow) => (
        <Input
          value={value}
          onChange={(e) => updateRow(record.id, 'price', e.target.value)}
          placeholder="e.g., 3.23/lb"
          size="small"
          status={value && !validatePrice(value) ? 'error' : undefined}
        />
      )
    },
    {
      title: 'Buyer Price',
      dataIndex: 'buyerPrice',
      width: 100,
      render: (value: string, record: ShipmentGoodsRow) => (
        <Input
          value={value}
          onChange={(e) => updateRow(record.id, 'buyerPrice', e.target.value)}
          size="small"
          placeholder=""
          style={{
            appearance: 'textfield',
            MozAppearance: 'textfield',
            WebkitAppearance: 'textfield'
          }}
        />
      )
    },
    {
      title: 'Origin',
      dataIndex: 'origin',
      width: 120,
      render: (value: string, record: ShipmentGoodsRow) => (
        <Input
          value={value}
          onChange={(e) => updateRow(record.id, 'origin', e.target.value)}
          placeholder="Origin"
          size="small"
        />
      )
    },
    {
      title: 'Clauses',
      width: 80,
      render: (_: any, record: ShipmentGoodsRow) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <Button
            type="primary"
            size="small"
            icon={<FileTextOutlined />}
            onClick={() => handleGenerateClauses(record.id)}
            style={{
              backgroundColor: colors.primary[60],
              borderColor: colors.primary[60],
              fontSize: '11px',
              height: '24px'
            }}
            disabled={!contractContext}
          >
            Generate
          </Button>
          {(record.shipmentNoteOne || record.shipmentNoteTwo || record.packingNotes) && (
            <div style={{
              fontSize: '10px',
              color: colors.status.positive,
              textAlign: 'center'
            }}>
              ✓ Has clauses
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Action',
      width: 60,
      render: (_: any, record: ShipmentGoodsRow) => (
        <Popconfirm
          title="Delete this shipment row?"
          onConfirm={() => removeRow(record.id)}
          disabled={rows.length <= 1}
        >
          <Button 
            type="text" 
            danger 
            size="small"
            icon={<DeleteOutlined />}
            disabled={rows.length <= 1}
          />
        </Popconfirm>
      )
    }
  ];

  return (
    <div onSubmit={(e) => e.preventDefault()}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: spacing.lg 
      }}>
        <SectionHeader title="Shipments & Goods" />
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={addRow}
          style={{ 
            backgroundColor: '#179DB5',
            borderColor: '#179DB5' 
          }}
        >
          Add Row
        </Button>
      </div>
      
      <Typography 
        variant="valueAddress" 
        style={{ 
          color: colors.text.secondary, 
          marginBottom: spacing.md,
          display: 'block'
        }}
      >
        Enter details in separate columns. For multiple goods per container, add separate rows with the same container details.
      </Typography>

      <div style={{ 
        overflowX: 'auto',
        border: `1px solid ${colors.border.default}`,
        borderRadius: '8px'
      }}>
        <Table
          columns={columns}
          dataSource={rows}
          pagination={false}
          rowKey="id"
          size="small"
          scroll={{ x: 1050 }}
          style={{ minWidth: '1050px' }}
        />
      </div>

      {/* Smart Snippet Builder Modal */}
      {shipmentContext && (
        <SmartSnippetBuilder
          visible={snippetBuilderVisible}
          onClose={() => {
            setSnippetBuilderVisible(false);
            setCurrentShipmentId(null);
            setShipmentContext(null);
          }}
          shipmentContext={shipmentContext}
          onSave={handleSaveClauses}
          initialClauses={currentShipmentId ? {
            shipmentNoteOne: rows.find(r => r.id === currentShipmentId)?.shipmentNoteOne,
            shipmentNoteTwo: rows.find(r => r.id === currentShipmentId)?.shipmentNoteTwo,
            packingNotes: rows.find(r => r.id === currentShipmentId)?.packingNotes
          } : undefined}
        />
      )}
    </div>
  );
};